import NumberFormat from "react-number-format";

// ---------------------------------------------------------------------------
// RentToMortgageCalculator.jsx — FORM (left column)
//
// LAYOUT CHANGE: Previously this file contained both the form inputs AND the
// results panel in a single card. The layout has been split into two components
// to mirror the main CalculationsPage.jsx two-column pattern:
//   - RentToMortgageCalculator.jsx  → form inputs (left column)
//   - RentToMortgageResults.jsx     → results panel (right column)
//   - RentToMortgagePage.jsx        → owns the grid layout and shared state
//
// Previously (single card, results below inputs):
//   return (
//     <div className="frost ... single card">
//       {/* inputs */}
//       {/* results grid — side-by-side inside same card */}
//     </div>
//   );
//
// Now: inputs only — results lifted out to RentToMortgageResults.jsx
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// calculateRentToMortgage
// Derived by reverse-engineering Rent_to_mortgage_calculator.xlsx.
//
// The spreadsheet treats monthly rent as the TARGET repayment, then
// back-calculates the loan principal via reverse amortisation:
//
//   Estimated Mortgage = PMT × [(1+r)^n - 1] / [r × (1+r)^n]
//     where PMT = monthlyRent, r = monthlyRate, n = termMonths
//
//   Minimum Income = estimatedMortgage ÷ 4.5  (standard UK income multiple)
//
// Returns: [estimatedMortgage, minimumIncome, monthlyRepayment, monthlyInterest]
// ---------------------------------------------------------------------------
export const calculateRentToMortgage = (
  monthlyRent,
  interestRate,
  termYears,
) => {
  if (!monthlyRent || !interestRate || !termYears) return [0, 0, 0, 0];

  // CHANGED: 4.5 → 5.5 to match the income multiple used throughout the app
  // Previously: const INCOME_MULTIPLE = 4.5;
  const INCOME_MULTIPLE = 5.5;
  const monthlyRate = interestRate / 100 / 12;
  const n = termYears * 12;

  const estimatedMortgage =
    monthlyRate === 0
      ? monthlyRent * n
      : monthlyRent *
        (((1 + monthlyRate) ** n - 1) / (monthlyRate * (1 + monthlyRate) ** n));

  const minimumIncome = estimatedMortgage / INCOME_MULTIPLE;
  const monthlyRepayment = monthlyRent; // by definition of reverse calc
  const monthlyInterest = estimatedMortgage * monthlyRate;

  return [
    Math.round(estimatedMortgage),
    Math.round(minimumIncome),
    Math.round(monthlyRepayment * 100) / 100,
    Math.round(monthlyInterest * 100) / 100,
  ];
};

// ---------------------------------------------------------------------------
// RentToMortgageCalculator — form inputs only
// Props: calculatorData, setCalculatorData
// Results are calculated in RentToMortgagePage and passed to RentToMortgageResults
// ---------------------------------------------------------------------------
function RentToMortgageCalculator({
  calculatorData,
  setCalculatorData,
  onReset,
}) {
  const { monthlyRent, interestRate, termYears } = calculatorData;

  // handleRangeChange — mirrors MortgageCalculator.jsx handler exactly
  const handleRangeChange = ({ currentTarget }) => {
    const input = {
      name: currentTarget.name,
      value: currentTarget.value.replace(/[^0-9.-]+/g, ""),
      min: currentTarget.min,
      max: currentTarget.max,
      maxLength: currentTarget.maxLength,
    };

    if (input.min && input.max) {
      input.value =
        Number(input.value) > Number(input.max)
          ? input.max
          : input.value !== "" && Number(input.value) < Number(input.min)
          ? input.min
          : input.value;
    }

    if (input.value.length > input.maxLength)
      input.value = input.value.substr(0, input.maxLength);

    const convertedValue = isNaN(parseFloat(input.value))
      ? 0
      : parseFloat(input.value);

    setCalculatorData((prev) => ({ ...prev, [input.name]: convertedValue }));
  };

  // handleRentChange — NumberFormat strips £ and commas before updating state
  const handleRentChange = ({ value }) => {
    const numeric = parseFloat(value.replace(/[^0-9.-]+/g, ""));
    setCalculatorData((prev) => ({
      ...prev,
      monthlyRent: isNaN(numeric) ? 0 : numeric,
    }));
  };

  // incrementButtons — mirrors MortgageCalculator.jsx pattern exactly
  const incrementButtons = (name, increment, maxLength, min, max) => {
    const handleMinus = () => {
      setCalculatorData((prev) => {
        const next = Math.max(min, prev[name] - increment);
        return {
          ...prev,
          [name]: Number(next.toString().substr(0, maxLength)),
        };
      });
    };
    const handlePlus = () => {
      setCalculatorData((prev) => {
        const next = Math.min(max, prev[name] + increment);
        return {
          ...prev,
          [name]: Number(next.toString().substr(0, maxLength)),
        };
      });
    };

    return (
      <div className="mt-1 flex w-full justify-between">
        <button
          type="button"
          className="inline-flex items-center rounded-full bg-gray-100 px-4 py-2 text-black hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-2 focus:ring-offset-white disabled:bg-gray-100/50 disabled:text-black/25 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-700 dark:focus:ring-gray-600 dark:focus:ring-offset-black dark:disabled:bg-gray-600/50 dark:disabled:text-white/25"
          disabled={calculatorData[name] <= min}
          onClick={handleMinus}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <button
          type="button"
          className="inline-flex items-center rounded-full bg-gray-100 px-4 py-2 text-black hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-2 focus:ring-offset-white disabled:bg-gray-100/50 disabled:text-black/25 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-700 dark:focus:ring-gray-600 dark:focus:ring-offset-black dark:disabled:bg-gray-600/50 dark:disabled:text-white/25"
          disabled={calculatorData[name] >= max}
          onClick={handlePlus}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="frost relative z-10 -mt-24 w-full rounded-lg border border-gray-200 bg-white p-4 pb-0 shadow-xl shadow-purple-500/25 sm:-mt-32">
      {/* CHANGED: mb-8 removed from outer div — grid gap-8 in RentToMortgagePage.jsx
          handles row spacing between all three cards uniformly.
          Previously: <div className="frost relative z-10 -mt-24 mb-8 w-full ..."> */}
      <p className="mb-4 text-lg">
        Enter your current monthly rent to see the equivalent mortgage you could
        afford.
      </p>

      {/* Monthly Rent */}
      <div className="mb-6">
        <label
          htmlFor="monthlyRentNumber"
          className="text-md mb-1 block font-medium text-black dark:text-white"
        >
          Monthly rent (£)
        </label>
        <div className="flex items-center">
          <div className="flex grow flex-col items-start">
            <input
              type="range"
              name="monthlyRent"
              id="monthlyRentRange"
              maxLength={6}
              min={100}
              max={10000}
              step={50}
              value={monthlyRent || ""}
              className="h-3 w-full appearance-none overflow-hidden rounded-lg border border-gray-400 bg-gray-100 focus:shadow-none focus:outline-none focus:ring-0 dark:border-gray-600 dark:bg-gray-900"
              onChange={handleRangeChange}
            />
          </div>
          <NumberFormat
            name="monthlyRent"
            id="monthlyRentNumber"
            maxLength={6}
            min={100}
            max={10000}
            value={monthlyRent === 0 ? "" : monthlyRent}
            className="text-md ml-4 block w-[8rem] rounded-md border border-gray-400 bg-white font-bold text-black focus:border-blueviolet focus:ring-blueviolet dark:border-gray-600 dark:bg-black dark:text-white"
            onValueChange={handleRentChange}
            thousandSeparator={true}
            prefix={"£"}
            decimalScale={0}
            allowNegative={false}
          />
        </div>
        {incrementButtons("monthlyRent", 50, 6, 100, 10000)}
      </div>

      {/* Interest Rate */}
      <div className="mb-6">
        <label
          htmlFor="interestRateNumber"
          className="text-md mb-1 block font-medium text-black dark:text-white"
        >
          Interest rate (%)
        </label>
        <div className="flex items-center">
          <input
            type="range"
            name="interestRate"
            id="interestRateRange"
            maxLength={4}
            min={0.01}
            max={10}
            step={0.01}
            value={interestRate || ""}
            className="h-3 w-full appearance-none overflow-hidden rounded-lg border border-gray-400 bg-gray-100 focus:shadow-none focus:outline-none focus:ring-0 dark:border-gray-600 dark:bg-gray-900"
            onChange={handleRangeChange}
          />
          <input
            type="number"
            name="interestRate"
            id="interestRateNumber"
            maxLength={4}
            min={0.01}
            max={10}
            step={0.01}
            value={interestRate === 0 ? "" : interestRate}
            className="text-md ml-4 block w-[6rem] rounded-md border border-gray-400 bg-white font-bold text-black focus:border-blueviolet focus:ring-blueviolet dark:border-gray-600 dark:bg-black dark:text-white"
            onChange={handleRangeChange}
          />
        </div>
        {incrementButtons("interestRate", 0.1, 4, 0.01, 10)}
      </div>

      {/* Mortgage Term */}
      {/* CHANGED: mb-8 → mb-4 to match SharedOwnershipResults reset button spacing */}
      {/* Previously: mb-0 (too tight — Reset touched + button) */}
      <div className="mb-4">
        <label
          htmlFor="termYearsNumber"
          className="text-md mb-1 block font-medium text-black dark:text-white"
        >
          Mortgage term (years)
        </label>
        <div className="flex items-center">
          <input
            type="range"
            name="termYears"
            id="termYearsRange"
            maxLength={2}
            min={1}
            max={40}
            step={1}
            value={termYears || ""}
            className="h-3 w-full appearance-none overflow-hidden rounded-lg border border-gray-400 bg-gray-100 focus:shadow-none focus:outline-none focus:ring-0 dark:border-gray-600 dark:bg-gray-900"
            onChange={handleRangeChange}
          />
          <input
            type="number"
            name="termYears"
            id="termYearsNumber"
            maxLength={2}
            min={1}
            max={40}
            step={1}
            value={termYears === 0 ? "" : termYears}
            className="text-md ml-4 block w-[6rem] rounded-md border border-gray-400 bg-white font-bold text-black focus:border-blueviolet focus:ring-blueviolet dark:border-gray-600 dark:bg-black dark:text-white"
            onChange={handleRangeChange}
          />
        </div>
        {incrementButtons("termYears", 1, 2, 1, 40)}
      </div>

      {/* Reset button — standard position: below sliders, in the form card */}
      {/* MOVED FROM: RentToMortgageResults.jsx */}
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          className="flex items-center rounded-full bg-gray-100 px-4 py-2 text-sm font-bold leading-none text-black hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-2 focus:ring-offset-white dark:bg-gray-600 dark:text-white dark:hover:bg-gray-700 dark:focus:ring-gray-600 dark:focus:ring-offset-black"
          onClick={onReset}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

export default RentToMortgageCalculator;
